/**
 * How much of the screen the world still has to itself.
 *
 * Panels sit over the world, and the character standing behind one is the worst
 * place for it to be. This works out the largest rectangle the interface is not
 * covering, which the camera then centres the player in.
 *
 * The panels are measured rather than assumed, so a layout change moves the
 * framing with it and nothing here has to know where anything is.
 */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Elements that count as covering the world.
 *
 * The chat log is deliberately absent. It grows and shrinks with every line that
 * arrives, and letting it decide the framing means the camera creeps every time
 * somebody speaks. It is also transparent and fades when nothing is happening,
 * so having the world behind it is the point rather than a problem.
 *
 * Bank and shop windows are absent for a different reason: the player put them
 * where they are. Moving the world because of a window somebody placed themselves
 * would fight the person who placed it.
 */
const PANELS =
  '.dock, .equipment, .hud-top-left, .hud-top-right, .touch-buttons, .editor-window, .editor-bar';

/**
 * Below this width the screen is small enough that a panel really is in the way.
 *
 * On anything wider there is room for both, and moving the world every time
 * somebody opens their bag is a camera that will not sit still. Panels are only
 * worked around where working around them is actually needed.
 */
const CROWDED_WIDTH = 1280;

/** Below this a rectangle is too small to frame anything in. */
const MINIMUM = 160;

const area = (rect: Rect): number => Math.max(0, rect.width) * Math.max(0, rect.height);

function overlaps(rect: Rect, other: Rect): boolean {
  return (
    rect.x < other.x + other.width &&
    other.x < rect.x + rect.width &&
    rect.y < other.y + other.height &&
    other.y < rect.y + rect.height
  );
}

/**
 * Cuts a panel out of a rectangle by trimming one side.
 *
 * Four cuts are possible and the roomiest is taken. Trimming rather than splitting
 * keeps the result a rectangle, which is all the camera can use, and keeps this
 * cheap enough to run whenever the interface changes.
 */
function trim(rect: Rect, panel: Rect): Rect {
  if (!overlaps(rect, panel)) return rect;

  const candidates: Rect[] = [
    { x: rect.x, y: rect.y, width: Math.max(0, panel.x - rect.x), height: rect.height },
    {
      x: panel.x + panel.width,
      y: rect.y,
      width: Math.max(0, rect.x + rect.width - (panel.x + panel.width)),
      height: rect.height,
    },
    { x: rect.x, y: rect.y, width: rect.width, height: Math.max(0, panel.y - rect.y) },
    {
      x: rect.x,
      y: panel.y + panel.height,
      width: rect.width,
      height: Math.max(0, rect.y + rect.height - (panel.y + panel.height)),
    },
  ];

  let best = candidates[0] ?? rect;
  for (const candidate of candidates) {
    if (area(candidate) > area(best)) best = candidate;
  }
  return best;
}

/** The largest part of the window no panel is drawn over. */
export function playableArea(always = false): Rect | null {
  if (typeof document === 'undefined') return null;

  const whole: Rect = { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight };

  // A wide screen has room for both, so the world sits still unless somebody has
  // asked for the character to be kept in the clear.
  if (!always && window.innerWidth >= CROWDED_WIDTH) return whole;

  let rect: Rect = whole;

  // Widest first: taking the biggest obstruction out while the area is still whole
  // gives a better result than nibbling at it from the edges.
  const panels = [...document.querySelectorAll(PANELS)]
    .map((element) => element.getBoundingClientRect())
    .map((box) => ({ x: box.x, y: box.y, width: box.width, height: box.height }))
    .filter((box) => box.width > 0 && box.height > 0)
    .sort((a, b) => area(b) - area(a));

  for (const panel of panels) {
    const trimmed = trim(rect, panel);
    // A panel that would leave nothing worth looking at is ignored instead, since
    // framing the player in a sliver is worse than framing them behind a panel.
    if (trimmed.width >= MINIMUM && trimmed.height >= MINIMUM) rect = trimmed;
  }

  return rect;
}
